/**
 * Shared enums, constants, and API contract types used by both the Express
 * server and the React client. Keep this small and dependency-free.
 */
// --- Enums (kept as const objects so they exist at runtime on both sides) ---
export const Role = {
    PLAYER: 'PLAYER',
    STAFF: 'STAFF',
};
export const GameStatus = {
    SCHEDULED: 'SCHEDULED',
    IN_PROGRESS: 'IN_PROGRESS',
    FINAL: 'FINAL',
    POSTPONED: 'POSTPONED',
    CANCELED: 'CANCELED',
};
export const WeekCalcStatus = {
    NOT_CALCULATED: 'NOT_CALCULATED',
    CALCULATED: 'CALCULATED',
};
/** Derived, human-facing lifecycle state of a week (computed, not stored). */
export const WeekState = {
    UPCOMING: 'UPCOMING',
    OPEN: 'OPEN',
    LOCKED: 'LOCKED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETE: 'COMPLETE',
};
// --- Constants -------------------------------------------------------------
export const WEEKLY_TOKEN_BYTES = 24; // 192 bits of entropy (>= 128 required)
/**
 * Decide which league button the `/play` chooser highlights, and order the
 * highlighted one first. The rule collapses the play-history cases: highlight
 * the league the player played last week if that's exactly one, else the sole
 * league they've ever played, else nothing (new player, or an ambiguous
 * both/neither history where no recent signal favours one).
 */
export function arrangePlayLeagues(leagues) {
    const soleMatch = (predicate) => {
        const matches = leagues.filter(predicate);
        return matches.length === 1 ? matches[0] : null;
    };
    const highlighted = soleMatch((l) => l.playedLastWeek) ?? soleMatch((l) => l.playedEver);
    return leagues
        .map((l) => ({ ...l, highlighted: l === highlighted }))
        .sort((a, b) => Number(b.highlighted) - Number(a.highlighted));
}
/**
 * Display-only proper-casing for person names: uppercases the first letter of
 * each whitespace-separated word and lowercases the rest, collapsing runs of
 * whitespace. Storage is never changed — this only affects what users see.
 * e.g. "cody smith" -> "Cody Smith", "john o'hara" -> "John O'hara".
 */
export function properCaseName(s) {
    return (s ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}
/** The one way to render a user's name. First+Last, else email local-part, else "Player". */
export function fullName(u) {
    const name = properCaseName(`${u.firstName ?? ''} ${u.lastName ?? ''}`);
    if (name)
        return name;
    const local = u.email?.split('@')[0]?.trim();
    return local || 'Player';
}
//# sourceMappingURL=index.js.map